import { type ButtonHTMLAttributes, forwardRef } from 'react';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'md' | 'sm';

const base =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-150 ' +
  'active:scale-[0.98] select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-conduril-light ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:opacity-60 disabled:pointer-events-none';

const sizes: Record<Size, string> = {
  md: 'px-4 py-2.5 text-sm min-h-[44px]',
  sm: 'px-3 py-2 text-[13px] min-h-[38px]',
};

const variants: Record<Variant, string> = {
  primary:
    'bg-gradient-to-b from-conduril-light to-conduril text-white shadow-btn hover:shadow-btnhover hover:brightness-[1.06]',
  secondary:
    'bg-white text-conduril-ink border border-conduril-border shadow-btn hover:bg-conduril-surface hover:border-conduril-soft',
  danger: 'bg-white text-danger-text border border-danger-border shadow-btn hover:bg-danger-bg',
  ghost: 'text-conduril hover:bg-conduril-tint',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className = '', type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
